import { CreateTradeSections } from "@shared/features/tradingTerminal/common/types";
import Tabs from "common/Tabs/Tabs";
import React, { useContext } from "react";
import styled, { useTheme } from "styled-components";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import { WebTradingTerminalContext } from "features/tradingTerminal/WebTradingTerminal.context";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import PageTabs from "common/Tabs/PageTabs.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

const SmartTradeForm = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const { sectionToComponent } = useContext(WebTradingTerminalContext)!;
  const { createTradeTab, setCreateTradeTab, tabWithErrors } = useContext(
    TradingTerminalContext
  )!;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isNewLook } = useTheme();

  const getTab = (tab: CreateTradeSections) => {
    if (tabWithErrors[tab] && isNewLook) {
      // New look: the tab keeps its size; a red label and dot flag errors.
      return (
        <TabErrorLabel>
          {t(tab)}
          <TabErrorDot aria-hidden={true} />
        </TabErrorLabel>
      );
    }
    if (tabWithErrors[tab]) {
      return (
        <Text
          as="div"
          type="danger"
          $size="S"
          $weight={typography.fontWeights.NORMAL}
          style={{ display: "flex", alignItems: "center" }}
        >
          {t(tab)}
        </Text>
      );
    }
    return t(tab);
  };

  let tabsItems = Object.values(CreateTradeSections).map((tab) => ({
    key: tab,
    label: getTab(tab),
  }));

  if (isBottomNavigator) {
    tabsItems = tabsItems.map((tabItem) => ({
      ...tabItem,
      children: (
        <TabContentWrapper>{sectionToComponent[tabItem.key]}</TabContentWrapper>
      ),
      forceRender: true,
    }));

    return (
      <PageTabs
        items={tabsItems}
        onTabChange={(tabItem) =>
          setCreateTradeTab(tabItem.key as CreateTradeSections)
        }
      />
    );
  }

  return (
    <>
      <StyledTabs
        activeKey={createTradeTab}
        onChange={(tab: string) =>
          setCreateTradeTab(tab as CreateTradeSections)
        }
        $isSmall={true}
        items={tabsItems}
      />
      {Object.entries(sectionToComponent).map(([iterTab, component]) => (
        <TabWrapper $visible={createTradeTab === iterTab} key={iterTab}>
          <ComponentWrapper>{component}</ComponentWrapper>
        </TabWrapper>
      ))}
    </>
  );
};

export default SmartTradeForm;

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab {
    padding-top: 0;
  }
  .ant-tabs-tab + .ant-tabs-tab {
    margin: 0 0 0 24px;
  }
  .ant-tabs-nav {
    margin-bottom: 0;
  }
  &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap {
    padding: 12px 12px 0;
  }
  .ant-tabs-content-holder {
    overflow-y: auto;
  }
  ${(p) =>
    p.theme.isNewLook &&
    `
    &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap {
      padding: 14px 16px 0;
    }
    .ant-tabs-tab {
      padding-bottom: 10px;
      font-size: 13px;
    }
  `}
`;
const TabErrorLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: ${(p) => p.theme.colors.red};
`;
const TabErrorDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.red};
`;
const ComponentWrapper = styled.div`
  overflow-y: auto;
  padding: 12px;
  position: relative;
  ${(p) => p.theme.isNewLook && `padding: 16px;`}
`;
const TabWrapper = styled.div<{
  $visible: boolean;
}>`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  user-select: none;
  ${(p) =>
    !p.$visible &&
    `
  display: none;
  opacity: 0;
  pointer-events: none;
`};
`;
const TabContentWrapper = styled.div`
  overflow-y: auto;
  padding: 12px;
  scrollbar-gutter: stable;
  max-height: 100%;
  @media (max-width: ${phoneMaxWidth}) {
    /* New look, phone: the one 16px page gutter. */
    ${(p) => p.theme.isNewLook && "padding: 12px 16px;"}
  }
`;
